import dayjs from 'dayjs';

import { FilteredNodes } from '@/features/canvas/contexts/editor-context';
import { components } from '@/types/api';

import { getInitiativeDates } from './getInitiativeDates';

type InitiativeApiResponse = components['schemas']['Initiative'];

export interface TreeNode {
  children: TreeNode[];
  depth: number;
  node: FilteredNodes;
  parent?: TreeNode;
}

export interface FlattenedTreeNode {
  depth: number;
  hasChildren: boolean;
  node: FilteredNodes;
}

/**
 * Set depth recursively for a tree node and all its descendants
 */
const setDepthRecursively = (node: TreeNode, depth: number): void => {
  // eslint-disable-next-line no-param-reassign
  node.depth = depth;
  node.children.forEach(child => {
    setDepthRecursively(child, depth + 1);
  });
};

/**
 * Sort tree nodes by their start date
 */
const sortTreeByDate = (nodes: TreeNode[]): void => {
  nodes.sort((a, b) => {
    const { startDate: startA } = getInitiativeDates(a.node.initiatives);
    const { startDate: startB } = getInitiativeDates(b.node.initiatives);

    if (!startA && !startB) return (b.node.id ?? 0) - (a.node.id ?? 0);
    if (!startA) return 1; // No date comes AFTER dated items
    if (!startB) return -1; // Item with date comes BEFORE dateless items

    const dateA = dayjs(startA);
    const dateB = dayjs(startB);

    if (dateA.isBefore(dateB)) return -1;
    if (dateA.isAfter(dateB)) return 1;
    return (b.node.id ?? 0) - (a.node.id ?? 0);
  });

  // Recursively sort children
  nodes.forEach(node => {
    if (node.children.length > 0) {
      sortTreeByDate(node.children);
    }
  });
};

/**
 * Build a tree structure from flat list of initiatives based on parent-child relationships
 */
export const buildInitiativeTree = (
  initiatives: FilteredNodes[],
  childrenMap: Map<number, number[]>
): TreeNode[] => {
  const nodeMap = new Map<number, TreeNode>();
  const roots: TreeNode[] = [];

  // First pass: Create TreeNode for each initiative
  initiatives.forEach(initiative => {
    if (initiative.initiatives?.id) {
      const treeNode: TreeNode = {
        node: initiative,
        children: [],
        depth: 0,
      };
      nodeMap.set(initiative.initiatives.id, treeNode);
    }
  });

  // Second pass: Build parent-child relationships
  nodeMap.forEach((treeNode, initiativeId) => {
    const childIds = childrenMap.get(initiativeId) || [];
    childIds.forEach(childId => {
      const childNode = nodeMap.get(childId);
      if (childNode) {
        treeNode.children.push(childNode);
        childNode.parent = treeNode;
      }
    });
  });

  // Third pass: Identify roots (nodes with no parent) and set depths
  nodeMap.forEach(treeNode => {
    if (!treeNode.parent) {
      roots.push(treeNode);
      setDepthRecursively(treeNode, 0);
    }
  });

  return roots;
};

/**
 * Flatten a tree into a list of visible nodes based on expanded state
 */
export const flattenTree = (roots: TreeNode[], expandedRows: Set<number>): FlattenedTreeNode[] => {
  const result: FlattenedTreeNode[] = [];

  const traverse = (node: TreeNode): void => {
    const initiativeId = node.node.initiatives?.id;

    // Add current node
    result.push({
      node: node.node,
      depth: node.depth,
      hasChildren: node.children.length > 0,
    });

    // Add children if expanded
    if (initiativeId && expandedRows.has(initiativeId) && node.children.length > 0) {
      node.children.forEach(child => traverse(child));
    }
  };

  roots.forEach(root => traverse(root));
  return result;
};

/**
 * Get all descendant IDs for a given initiative
 */
export const getDescendantIds = (
  initiativeId: number,
  childrenMap: Map<number, number[]>
): Set<number> => {
  const descendants = new Set<number>();

  const collectDescendants = (id: number): void => {
    const children = childrenMap.get(id) || [];
    children.forEach(childId => {
      descendants.add(childId);
      collectDescendants(childId);
    });
  };

  collectDescendants(initiativeId);
  return descendants;
};

/**
 * Create a map of parent IDs to their children's data from API responses
 */
export const createParentChildMap = (
  childrenQueries: { data?: InitiativeApiResponse[]; queryKey?: (string | number)[] }[]
): Map<number, InitiativeApiResponse[]> => {
  const parentChildMap = new Map<number, InitiativeApiResponse[]>();

  childrenQueries.forEach(query => {
    if (query.data && query.data.length > 0) {
      // The query key contains the parent initiative ID
      const parentId = query.queryKey?.[1];
      if (parentId && typeof parentId === 'number') {
        parentChildMap.set(parentId, query.data);
      }
    }
  });

  return parentChildMap;
};

/**
 * Build tree for a section with proper parent-child relationships
 */
export const buildSectionTree = (
  sectionNodes: FilteredNodes[],
  parentChildData: Map<number, InitiativeApiResponse[]>
): TreeNode[] => {
  const nodeMap = new Map<number, TreeNode>();
  const childToParentMap = new Map<number, number>();
  const roots: TreeNode[] = [];

  // First, build child-to-parent mapping from the API data
  parentChildData.forEach((children, parentId) => {
    children.forEach(child => {
      if (child.id) {
        childToParentMap.set(child.id, parentId);
      }
    });
  });

  // Create TreeNode for each node in section
  sectionNodes.forEach(node => {
    if (node.initiatives?.id) {
      const treeNode: TreeNode = {
        node,
        children: [],
        depth: 0,
      };
      nodeMap.set(node.initiatives.id, treeNode);
    }
  });

  // Build parent-child relationships - only for nodes that are BOTH in this section
  nodeMap.forEach((treeNode, nodeInitiativeId) => {
    const children = parentChildData.get(nodeInitiativeId) || [];
    children.forEach(childInitiative => {
      // Find the corresponding node in our section
      const childNode = sectionNodes.find(n => n.initiatives?.id === childInitiative.id);
      if (childNode?.initiatives?.id) {
        const childTreeNode = nodeMap.get(childNode.initiatives.id);
        if (childTreeNode) {
          treeNode.children.push(childTreeNode);
          childTreeNode.parent = treeNode;
        }
      }
    });
  });

  // Identify roots - an initiative is a root if:
  // 1. It has no parent established in the tree above, OR
  // 2. It has a parent but the parent is not in this section (filtered out)
  nodeMap.forEach(treeNode => {
    if (!treeNode.parent) {
      // Either truly no parent, or parent is in different section
      roots.push(treeNode);
      setDepthRecursively(treeNode, 0);
    }
  });

  // Fallback: if no roots were found but we have nodes, treat all nodes as roots
  // This can happen when parent-child data is still loading or empty
  if (roots.length === 0 && nodeMap.size > 0) {
    nodeMap.forEach(treeNode => {
      roots.push(treeNode);
      setDepthRecursively(treeNode, 0);
    });
  }

  // Sort roots and children by start date
  sortTreeByDate(roots);

  return roots;
};
